import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { 
  Box, 
  Button, 
  Container, 
  TextField, 
  Typography, 
  Paper, 
  Avatar, 
  Alert,
  Grid,
  IconButton
} from '@mui/material';
import { VerifiedUser as VerifiedUserIcon, Backspace as BackspaceIcon } from '@mui/icons-material';
import { useAuth } from '../../contexts/AuthContext';
import { isAuthSessionValid } from '../../utils/auth';

const PasscodeVerificationPage: React.FC = () => {
  const [passcode, setPasscode] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const { verifyPasscode, userProfile } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  
  // Get the return URL from location state or default to launch
  const returnTo = location.state?.returnTo || '/staff/launch';
  
  // If already authenticated, redirect to launch
  useEffect(() => {
    if (isAuthSessionValid()) {
      navigate(returnTo);
    }
  }, [navigate, returnTo]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!passcode) {
      setError('Please enter your passcode');
      return;
    }
    
    try {
      setError('');
      setLoading(true);
      
      const isValid = await verifyPasscode(passcode);
      
      if (isValid) {
        navigate(returnTo);
      } else {
        setError('Invalid passcode. Please try again.');
      }
    } catch (err: any) {
      setError(err.message || 'Failed to verify passcode');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  // Keypad handler functions
  const handleKeypadPress = (digit: string) => {
    setPasscode(prev => prev + digit);
  };

  const handleBackspace = () => {
    setPasscode(prev => prev.slice(0, -1));
  };

  const handleClear = () => {
    setPasscode('');
  };

  // Keypad component
  const NumericKeypad = () => (
    <Box sx={{ mt: 2, width: '100%' }}>
      <Grid container spacing={1} justifyContent="center">
        {[1, 2, 3, 4, 5, 6, 7, 8, 9].map(digit => (
          <Grid key={digit} item xs={4}>
            <Button
              variant="outlined"
              fullWidth
              sx={{ 
                height: 56, 
                fontSize: '1.25rem',
                border: '1px solid rgba(0, 0, 0, 0.23)',
                '&:hover': {
                  backgroundColor: 'rgba(25, 118, 210, 0.04)',
                  border: '1px solid rgba(25, 118, 210, 0.5)'
                }
              }}
              onClick={() => handleKeypadPress(digit.toString())}
            >
              {digit}
            </Button>
          </Grid>
        ))}
        <Grid item xs={4}>
          <Button
            variant="outlined"
            fullWidth
            sx={{ 
              height: 56, 
              fontSize: '1.25rem',
              color: 'error.main',
              border: '1px solid rgba(0, 0, 0, 0.23)',
              '&:hover': {
                backgroundColor: 'rgba(211, 47, 47, 0.04)',
                border: '1px solid rgba(211, 47, 47, 0.5)'
              }
            }}
            onClick={handleClear}
          >
            C
          </Button>
        </Grid>
        <Grid item xs={4}>
          <Button
            variant="outlined"
            fullWidth
            sx={{ 
              height: 56, 
              fontSize: '1.25rem',
              border: '1px solid rgba(0, 0, 0, 0.23)',
              '&:hover': {
                backgroundColor: 'rgba(25, 118, 210, 0.04)',
                border: '1px solid rgba(25, 118, 210, 0.5)'
              }
            }}
            onClick={() => handleKeypadPress('0')}
          >
            0
          </Button>
        </Grid>
        <Grid item xs={4}>
          <Button
            variant="outlined"
            fullWidth
            sx={{ 
              height: 56, 
              border: '1px solid rgba(0, 0, 0, 0.23)',
              '&:hover': {
                backgroundColor: 'rgba(25, 118, 210, 0.04)',
                border: '1px solid rgba(25, 118, 210, 0.5)'
              }
            }}
            onClick={handleBackspace}
          >
            <BackspaceIcon />
          </Button>
        </Grid>
      </Grid>
    </Box>
  );

  return (
    <Container component="main" maxWidth="xs">
      <Paper elevation={3} sx={{ p: 4, mt: 8, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
        <Avatar sx={{ m: 1, bgcolor: 'secondary.main' }}>
          <VerifiedUserIcon />
        </Avatar>
        <Typography component="h1" variant="h5">
          Launch Ramp Access
        </Typography>
        
        <Typography variant="body1" sx={{ mt: 2, textAlign: 'center' }}>
          Hello, {userProfile?.firstName}! Please enter your passcode to access the launch ramp page.
        </Typography>
        
        {error && (
          <Alert severity="error" sx={{ width: '100%', mt: 2 }}>
            {error}
          </Alert>
        )}
        
        <Box component="form" onSubmit={handleSubmit} sx={{ mt: 3, width: '100%' }}>
          <TextField
            required
            fullWidth
            name="passcode"
            label="Passcode"
            type="password"
            id="passcode"
            autoComplete="current-passcode"
            value={passcode}
            onChange={(e) => setPasscode(e.target.value)}
            sx={{ mb: 2 }}
            inputProps={{ 
              style: { fontSize: '1.5rem', letterSpacing: '0.5rem', textAlign: 'center' },
              maxLength: 8 
            }}
          />
          
          {/* Numeric Keypad */}
          <NumericKeypad />
          
          <Button
            type="submit"
            fullWidth
            variant="contained"
            sx={{ mt: 3, mb: 2 }}
            disabled={loading}
          >
            {loading ? 'Verifying...' : 'Verify Passcode'}
          </Button>
        </Box>
      </Paper>
    </Container>
  );
};

export default PasscodeVerificationPage; 